import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { mobileBreakpoint, useIsMobile, useL10n } from '@uplift/hooks';
import { Button, CreditCardValidator, Indicator } from '@uplift/ui-v2';

import { useBillingContext } from '../billing.context';
import { useUserBillingQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { useFormikContext } from 'formik';
import { BillingFormType } from '../billing-form';


const useStyles = makeStyles()((theme) => ({
  semiBold: {
    fontWeight: 500
  },
  creditCard: {
    marginBottom: theme.spacing(-1),
    marginRight: theme.spacing(1),
    '& svg': {
      transform: 'scale(1.3)',
    }
  },
  dividerTop: {
    borderTop: `1px solid ${theme.palette.divider}`
  },
  indicator: {
    marginRight: theme.spacing(0.5),
  },
}));


export type PaymentDetailsCardProps = {
  showAutopay?: boolean;
};

export const PaymentDetailsCard: React.FC<PaymentDetailsCardProps> = ({ showAutopay }) => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const { classes } = useStyles();
  const formikContext = useFormikContext<BillingFormType>();
  const isMobile = useIsMobile(mobileBreakpoint);

  const { cardEndingIn, handleEditPaymentMethod, setPaymentsToPay } = useBillingContext();
  const { data: billingData } = useUserBillingQuery(me.id, { suspense: true });

  return (
    <Paper
      variant={isMobile ? 'outlined' : 'elevation'}
      elevation={isMobile ? 0 : 1}
      square
    >
      <Box display="flex" p={3}>
        <Box display="flex" flexGrow={1} gap="4px">
          <Box display="flex" flexDirection="column" justifyContent="center">
            <CreditCardValidator singleCard cardNumber={billingData?.last4} className={classes.creditCard} />
          </Box>
          <Box display="flex" gap="8px" flexDirection="column">
            <Box>
              <Typography color="textPrimary" variant="body1">
                {cardEndingIn}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography variant="body2" color="textSecondary">
                {l10n.accountSettings.billing.expires}&nbsp;
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {billingData?.expiration.format('MM/YY')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          onClick={() => {
            setPaymentsToPay(formikContext.values.payments);
            handleEditPaymentMethod();
          }}
          variant="text"
          color="secondary"
        >
          {l10n.general.edit}
        </Button>
      </Box>
      {showAutopay && (
        <Box display="flex" p={3} gap="16px" className={classes.dividerTop} >
          <Typography variant="body1">
            {l10n.accountSettings.billing.autopay}
          </Typography>
          <Typography variant="body1" className={classes.semiBold}>
            <Indicator color="success" size="ml" className={classes.indicator} /> {l10n.general.on}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
